import { useMemo, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermissions } from '@/hooks/use-permissions';
import { KnowledgeItemsView } from '@/modules/knowledge-promotion';
import { KnowledgeIntentsTable, KnowledgeIntentDetailsSidebar } from '@/modules/promoted-blueprints';
import { groupKnowledgeByIntent } from '@/modules/promoted-blueprints/utils';
import { useKnowledgeBlueprints } from '@/hooks';
import type { KnowledgeIntentGroup } from '@/modules/promoted-blueprints';

type View = 'items' | 'intent';

export default function PromotedKnowledge() {
  const { can } = usePermissions();
  const [view, setView] = useState<View>('items');
  const { data: knowledgeBlueprints, isLoading: knowledgeLoading } = useKnowledgeBlueprints();
  const [selectedKnowledgeIntent, setSelectedKnowledgeIntent] = useState<string | null>(null);

  const knowledgeGroups = useMemo(
    () => groupKnowledgeByIntent(knowledgeBlueprints ?? []),
    [knowledgeBlueprints]
  );

  const openKnowledgeIntent = (group: KnowledgeIntentGroup) => {
    setSelectedKnowledgeIntent(group.intent);
  };

  const selectedKnowledgeGroup = selectedKnowledgeIntent
    ? knowledgeGroups.find((group) => group.intent === selectedKnowledgeIntent)
    : undefined;

  return (
    <div className='flex h-full'>
      <div className='flex flex-1 flex-col gap-6 overflow-y-auto p-4'>
        <div className='space-y-1'>
          <h1 className='text-base font-semibold'>Promoted Knowledge</h1>
          <p className='text-sm text-muted-foreground'>
            Answers the agent serves directly. Make them from an article or your own question and answer pairs, check them,
            and promote them to serve as drafts. An answer auto-sends only after agents send enough of its drafts unchanged.
          </p>
        </div>

        <Tabs value={view} onValueChange={(value) => setView(value as View)}>
          <TabsList>
            <TabsTrigger value='items'>Questions and answers</TabsTrigger>
            <TabsTrigger value='intent'>By intent</TabsTrigger>
          </TabsList>
        </Tabs>

        {view === 'items' ? (
          <KnowledgeItemsView canEdit={can('edit', 'promoted-blueprint')} />
        ) : (
          <div className='flex flex-col gap-5'>
            <p className='rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground'>
              <span className='font-medium text-foreground'>Knowledge intents</span> = grouped Q&amp;A. Only
              promoted answers serve: promote them in Questions and answers. The switch pauses or resumes an
              intent&apos;s promoted answers. Open an intent to see its queries.
            </p>
            <KnowledgeIntentsTable
              knowledgeBlueprints={knowledgeBlueprints ?? []}
              isLoading={knowledgeLoading}
              onRowClick={openKnowledgeIntent}
            />
          </div>
        )}
      </div>

      {view === 'intent' && selectedKnowledgeIntent && (
        <KnowledgeIntentDetailsSidebar
          intent={selectedKnowledgeIntent}
          queries={selectedKnowledgeGroup?.queries ?? []}
          onClose={() => setSelectedKnowledgeIntent(null)}
        />
      )}
    </div>
  );
}
